import { useState, useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Sidebar } from './sidebar'
import { Header } from './header'
import { useAuth } from '@/components/auth'
import { TwoFactorAuthService } from '@/domain/two-factor-auth/two-factor-auth.service'
import { AlertTriangle, X, Smartphone, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'

export function AppLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const { user, sessionToken } = useAuth()
  const navigate = useNavigate()
  const [show2FAWarning, setShow2FAWarning] = useState(false)
  const [isChecking2FA, setIsChecking2FA] = useState(true)

  // ✅ ตรวจสอบว่า Admin ต้อง setup 2FA หรือไม่
  useEffect(() => {
    const check2FARequirement = async () => {
      if (!user || !sessionToken) {
        setIsChecking2FA(false)
        return
      }

      try {
        // ตรวจสอบ localStorage flag
        const setupRequired = localStorage.getItem('admin_2fa_setup_required') === 'true'
        if (!setupRequired) {
          setIsChecking2FA(false)
          return
        }

        // ตรวจสอบ 2FA status จาก server
        const twoFactorCheck = await TwoFactorAuthService.checkRequirement(user.id)
        
        if (twoFactorCheck.require2FAForAdmin && twoFactorCheck.isAdmin && !twoFactorCheck.isEnabled) {
          // ยังไม่ได้ setup → แสดง warning
          setShow2FAWarning(true)
        } else {
          // setup แล้ว → ลบ flag
          localStorage.removeItem('admin_2fa_setup_required')
          localStorage.removeItem('admin_2fa_setup_required_time')
          setShow2FAWarning(false)
        }
      } catch (error) {
        console.error('[AppLayout] Check 2FA requirement error:', error)
      } finally {
        setIsChecking2FA(false)
      }
    }

    check2FARequirement()
  }, [user, sessionToken])

  // ✅ Auto-hide warning หลัง 24 ชั่วโมง (grace period)
  useEffect(() => {
    if (!show2FAWarning) return

    const setupTime = localStorage.getItem('admin_2fa_setup_required_time')
    if (setupTime) {
      const elapsed = Date.now() - parseInt(setupTime, 10)
      const hours24 = 24 * 60 * 60 * 1000
      
      if (elapsed > hours24) {
        // เกิน 24 ชั่วโมงแล้ว → ซ่อน warning (แต่ยังต้อง setup)
        setShow2FAWarning(false)
      }
    }
  }, [show2FAWarning])

  const handleSetup2FA = () => {
    navigate('/profile?tab=security')
  }

  const handleDismissWarning = () => {
    setShow2FAWarning(false)
    // เก็บ flag ไว้เพื่อตรวจสอบอีกครั้งในครั้งถัดไป
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar collapsed={collapsed} onCollapse={setCollapsed} />
      <Header collapsed={collapsed} />
      <main
        className={cn(
          "min-h-screen pt-16 transition-all duration-300",
          collapsed ? "pl-16" : "pl-64"
        )}
      >
        {/* ✅ 2FA Setup Warning Banner */}
        {show2FAWarning && !isChecking2FA && (
          <div className="sticky top-16 z-30 mx-6 mt-6">
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Smartphone className="h-4 w-4 text-amber-600" />
                        <h3 className="font-semibold text-amber-900 dark:text-amber-100">
                          ต้องตั้งค่า 2FA
                        </h3>
                      </div>
                      <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                        ระบบบังคับให้ Admin ต้องเปิดใช้งาน 2FA เพื่อความปลอดภัย
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        กรุณาตั้งค่า 2FA ภายใน 24 ชั่วโมง
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        onClick={handleSetup2FA}
                        size="sm"
                        className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        ไปตั้งค่า 2FA
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                        onClick={handleDismissWarning}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className={cn("p-6", show2FAWarning && !isChecking2FA && "pt-2")}>
          <Outlet />
        </div>
      </main>
    </div>
  )
}















